/**
 * Карточка «Движа» (P-01, изменение v1.3) — не анкета, а предложение плана.
 *
 * Сверху кадр с диагональным срезом (наклон знака «Движ»), под ним плотная подложка:
 * имя, одна фраза подбора, общие интересы впереди остальных и строка плана —
 * что делаем и когда оба свободны. Слева на кадре — полоса совместимости (три линии
 * скорости из знака), нажатие открывает «Почему вы сойдётесь».
 *
 * Главный способ листать — свайп: карточка идёт за пальцем, кренится в сторону движения,
 * из-за края выезжает «ГО» или «МИМО», за карточкой тянутся линии скорости. Кнопки в
 * нижней рейке делают то же самое и остаются равноценным способом (доступность,
 * desktop, `prefers-reduced-motion`). Оценка привлекательности не показывается никогда
 * (ПС §09 шаг 8): совместимость — это про пару.
 */
import { useEffect, useRef, useState } from 'react'

import type { RhythmSlot, UserCard } from '@shared/api/types'
import { VelocityTracker, clamp, haptic, isTouchLike, reducedMotion } from '@shared/lib/motion'
import { Icon, Spinner, cx } from '@shared/ui'

import styles from './CandidateCard.module.css'
import { CompatMeter } from './CompatMeter'
import { RHYTHM_LABEL } from './vibe'

/** ПС §09: в карточке показываем 3–5 ключевых интересов, остальное — в профиле. */
const VISIBLE_INTERESTS = 5

/*
 * Свайп карточки пальцем: вправо — «Го», влево — «Мимо». Порог — четверть ширины или
 * быстрый взмах; до порога карточка возвращается пружиной. Крен зависит от точки
 * захвата: взял сверху — карточка заваливается сильнее, как настоящая.
 */
const SWIPE_ACTIVATE_PX = 8
const SWIPE_COMMIT_RATIO = 0.26
/** px/мс */
const SWIPE_COMMIT_VELOCITY = 0.55
const MAX_TILT_DEG = 14
const FLY_MS = 320
const SPRING_MS = 420

interface CardSwipe {
  pointerId: number
  startX: number
  startY: number
  active: boolean
  dx: number
  dy: number
  width: number
  /** +1 — палец лёг выше середины карточки, −1 — ниже: от этого зависит знак крена. */
  tiltSign: 1 | -1
  pastThreshold: boolean
  tracker: VelocityTracker
  frame: number
}

/** То, что viewer рассказал о себе, — чтобы подсветить общее прямо на карточке. */
export interface ViewerContext {
  interest_ids: readonly string[]
  formats: readonly string[]
  rhythm: readonly RhythmSlot[]
}

interface CandidateCardProps {
  card: UserCard
  viewer?: ViewerContext
  /** «Движ дня» — верх подборки «Для тебя». */
  pick?: boolean
  busy?: boolean
  leaving?: boolean
  /** Карточка на вершине колоды — единственная, которая отвечает на жесты и кнопки. */
  interactive?: boolean
  /** Показать подсказку о свайпе: карточка качается, под ней объяснение. */
  coach?: boolean
  className?: string
  onLike: () => void
  onPass: () => void
  onOpen: () => void
  onMore: (anchor: DOMRect) => void
  /** Открыть «Почему вы сойдётесь». */
  onWhy?: () => void
  /** Ход жеста −1…1 (знак — сторона): колода подтягивает следующую карточку и красит сцену. */
  onDrag?: (drag: number) => void
  /** Человек взялся за карточку — подсказка больше не нужна. */
  onGestureStart?: () => void
}

export function CandidateCard({
  card,
  viewer,
  pick = false,
  busy = false,
  leaving = false,
  interactive = true,
  coach = false,
  className,
  onLike,
  onPass,
  onOpen,
  onMore,
  onWhy,
  onDrag,
  onGestureStart,
}: CandidateCardProps): React.ReactElement {
  const [photoIndex, setPhotoIndex] = useState(0)
  const cardRef = useRef<HTMLElement>(null)
  const swipeRef = useRef<CardSwipe | null>(null)
  const suppressClickRef = useRef(false)
  /** Карточка улетела жестом или кнопкой; если действие не удалось, вернуть её на место. */
  const flownRef = useRef(false)

  useEffect(() => {
    const element = cardRef.current
    if (leaving || !flownRef.current || !element) return
    flownRef.current = false
    element.style.transition = ''
    element.style.transform = ''
    element.style.opacity = ''
    element.style.removeProperty('--stamp-like')
    element.style.removeProperty('--stamp-pass')
    onDrag?.(0)
  }, [leaving, onDrag])

  const paint = (dx: number, dy: number, tiltSign: 1 | -1, animate: string | null): void => {
    const element = cardRef.current
    if (!element) return
    const width = element.clientWidth || 320
    const tilt = clamp((dx / width) * MAX_TILT_DEG * 2.2, -MAX_TILT_DEG, MAX_TILT_DEG) * tiltSign
    element.style.transition = animate ?? 'none'
    element.style.transform = dx || dy ? `translate3d(${dx}px, ${dy * 0.35}px, 0) rotate(${tilt}deg)` : ''
    element.style.setProperty('--stamp-like', String(clamp(dx / (width * 0.22), 0, 1)))
    element.style.setProperty('--stamp-pass', String(clamp(-dx / (width * 0.22), 0, 1)))
  }

  /** Карточка улетает в сторону действия; само действие уходит следом. */
  const flyOut = (direction: 1 | -1): void => {
    if (flownRef.current || busy || leaving) return
    const element = cardRef.current
    const action = direction > 0 ? onLike : onPass
    if (!element || reducedMotion()) {
      action()
      return
    }
    flownRef.current = true
    haptic('medium')
    const width = element.clientWidth || 320
    element.style.transition = `transform ${FLY_MS}ms var(--ease-ios), opacity ${FLY_MS}ms var(--ease-out)`
    element.style.transform = `translate3d(${direction * width * 1.25}px, -24px, 0) rotate(${direction * MAX_TILT_DEG}deg)`
    element.style.opacity = '0'
    element.style.setProperty(direction > 0 ? '--stamp-like' : '--stamp-pass', '1')
    onDrag?.(direction)
    setTimeout(action, FLY_MS * 0.55)
  }

  const onPointerDown = (event: React.PointerEvent<HTMLElement>): void => {
    if (!interactive || busy || leaving || swipeRef.current || !isTouchLike(event) || !event.isPrimary) return
    if (reducedMotion()) return
    const box = event.currentTarget.getBoundingClientRect()
    const tracker = new VelocityTracker()
    tracker.push(event.clientX, event.clientY)
    swipeRef.current = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      active: false,
      dx: 0,
      dy: 0,
      width: box.width || 1,
      tiltSign: event.clientY < box.top + box.height / 2 ? 1 : -1,
      pastThreshold: false,
      tracker,
      frame: 0,
    }
  }

  const onPointerMove = (event: React.PointerEvent<HTMLElement>): void => {
    const swipe = swipeRef.current
    if (!swipe || event.pointerId !== swipe.pointerId) return

    const dx = event.clientX - swipe.startX
    const dy = event.clientY - swipe.startY
    swipe.tracker.push(event.clientX, event.clientY)

    if (!swipe.active) {
      if (Math.abs(dy) > SWIPE_ACTIVATE_PX && Math.abs(dy) > Math.abs(dx)) {
        swipeRef.current = null
        return
      }
      if (Math.abs(dx) < SWIPE_ACTIVATE_PX) return
      swipe.active = true
      suppressClickRef.current = true
      onGestureStart?.()
      cardRef.current?.classList.add(styles['dragging'] ?? '')
      event.currentTarget.setPointerCapture(event.pointerId)
    }

    swipe.dx = dx
    swipe.dy = dy
    const past = Math.abs(dx) > swipe.width * SWIPE_COMMIT_RATIO
    if (past !== swipe.pastThreshold) {
      swipe.pastThreshold = past
      if (past) haptic('light')
    }

    cancelAnimationFrame(swipe.frame)
    swipe.frame = requestAnimationFrame(() => {
      paint(dx, dy, swipe.tiltSign, null)
      onDrag?.(clamp(dx / (swipe.width * SWIPE_COMMIT_RATIO), -1, 1))
    })
  }

  const finishSwipe = (event: React.PointerEvent<HTMLElement>): void => {
    const swipe = swipeRef.current
    if (!swipe || event.pointerId !== swipe.pointerId) return
    swipeRef.current = null
    cardRef.current?.classList.remove(styles['dragging'] ?? '')
    if (!swipe.active) return
    cancelAnimationFrame(swipe.frame)

    const velocity = swipe.tracker.velocity().x
    const direction = Math.sign(swipe.dx || velocity)
    const commit =
      Math.abs(velocity) > SWIPE_COMMIT_VELOCITY
        ? Math.sign(velocity) === direction
        : Math.abs(swipe.dx) > swipe.width * SWIPE_COMMIT_RATIO

    if (commit && direction !== 0) {
      flyOut(direction > 0 ? 1 : -1)
      return
    }

    paint(0, 0, swipe.tiltSign, `transform ${SPRING_MS}ms var(--ease-spring)`)
    onDrag?.(0)
    setTimeout(() => {
      if (cardRef.current && !swipeRef.current) cardRef.current.style.transition = ''
    }, SPRING_MS)
  }

  const onClickCapture = (event: React.MouseEvent<HTMLElement>): void => {
    if (!suppressClickRef.current) return
    suppressClickRef.current = false
    event.preventDefault()
    event.stopPropagation()
  }

  const photos = card.photos
  const photo = photos[Math.min(photoIndex, Math.max(0, photos.length - 1))]
  const photoUrl = photo?.variants.find((v) => v.name === 'medium')?.url ?? photo?.variants[0]?.url

  const vibe = card.vibe ?? null
  const name = card.display_name ?? 'Профиль недоступен'

  /* Общие интересы — первыми и с галочкой; остальные следом */
  const sharedIds = new Set(vibe?.shared_interest_ids ?? viewer?.interest_ids ?? [])
  const allInterests = card.interests ?? []
  const ordered = [
    ...allInterests.filter((i) => sharedIds.has(i.id)),
    ...allInterests.filter((i) => !sharedIds.has(i.id)),
  ]
  const interests = ordered.slice(0, VISIBLE_INTERESTS)
  const restInterests = allInterests.length - interests.length

  const plan = planLine(card, viewer)

  return (
    <article
      ref={cardRef}
      className={cx(
        styles['card'],
        leaving && styles['leaving'],
        !interactive && styles['inactive'],
        coach && interactive && styles['coach'],
        className,
      )}
      data-band={vibe?.band}
      aria-hidden={interactive ? undefined : true}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={finishSwipe}
      onPointerCancel={finishSwipe}
      onClickCapture={onClickCapture}
    >
      {/* Кадр с диагональным срезом: наклон повторяет знак «Движ» */}
      <div className={styles['frame']}>
        {photoUrl ? (
          <img
            key={photo?.id}
            className={styles['photo']}
            src={photoUrl}
            alt={`Фото: ${card.display_name ?? ''}`}
          />
        ) : (
          <div className={styles['photoEmpty']} aria-hidden>
            <Icon name="user" size={56} />
          </div>
        )}

        <div className={styles['frameTint']} aria-hidden />

        {photos.length > 1 ? (
          <>
            <div className={styles['photoNav']} aria-hidden>
              {photos.map((item, index) => (
                <span
                  key={item.id}
                  className={cx(styles['photoDot'], index === photoIndex && styles['photoDotActive'])}
                />
              ))}
            </div>

            {/* Половины кадра листают фото — привычный жест карточных лент */}
            <button
              type="button"
              className={cx(styles['photoHalf'], styles['photoHalfLeft'])}
              aria-label="Предыдущее фото"
              tabIndex={interactive ? 0 : -1}
              onClick={() => setPhotoIndex((i) => Math.max(0, i - 1))}
            />
            <button
              type="button"
              className={cx(styles['photoHalf'], styles['photoHalfRight'])}
              aria-label="Следующее фото"
              tabIndex={interactive ? 0 : -1}
              onClick={() => setPhotoIndex((i) => Math.min(photos.length - 1, i + 1))}
            />
          </>
        ) : null}

        <div className={styles['frameTop']}>
          <div className={styles['frameTopStart']}>
            {pick ? (
              <span className={styles['pick']}>
                <Icon name="bolt" size={12} />
                Движ дня
              </span>
            ) : null}
            {vibe ? (
              <CompatMeter vibe={vibe} onPhoto onClick={onWhy} tabIndex={interactive ? 0 : -1} />
            ) : null}
          </div>

          <button
            type="button"
            className={styles['moreButton']}
            aria-label="Ещё действия"
            tabIndex={interactive ? 0 : -1}
            onClick={(event) => onMore(event.currentTarget.getBoundingClientRect())}
          >
            <Icon name="more" size={20} />
          </button>
        </div>

        {card.presence?.online ? (
          <span className={styles['online']}>
            <span className={styles['onlineDot']} />В сети
          </span>
        ) : null}
      </div>

      {/* Подложка: кто это и что с ним можно сделать */}
      <div className={styles['panel']}>
        <button
          type="button"
          className={styles['identity']}
          aria-label={`Открыть профиль: ${name}`}
          tabIndex={interactive ? 0 : -1}
          onClick={onOpen}
        >
          <span className={styles['name']}>{name}</span>
          {card.age !== null ? <span className={styles['age']}>{card.age}</span> : null}
          <span className={styles['place']}>{card.city ? card.city.name : null}</span>
          {/* Строка имени ведёт в профиль — шеврон говорит это прямее, чем «i» */}
          <span className={styles['infoIcon']} aria-hidden>
            <Icon name="chevron-right" size={16} />
          </span>
        </button>

        {vibe ? (
          <p className={styles['headline']}>
            <Icon name="sparkle" size={15} />
            <span>{vibe.headline}</span>
          </p>
        ) : card.bio ? (
          <p className={styles['headline']}>
            <span>{card.bio}</span>
          </p>
        ) : null}

        {interests.length ? (
          <div className={styles['tags']}>
            {interests.map((interest) => {
              const shared = sharedIds.has(interest.id)
              return (
                <span
                  key={interest.id}
                  className={cx(styles['tag'], shared && styles['tagShared'])}
                  title={shared ? 'Общий интерес' : undefined}
                >
                  {shared ? <Icon name="check" size={11} /> : null}
                  {interest.label}
                </span>
              )
            })}
            {restInterests > 0 ? <span className={styles['tag']}>+{restInterests}</span> : null}
          </div>
        ) : null}

        {plan ? (
          <p className={styles['plan']}>
            <span className={styles['planWhat']}>{plan.what}</span>
            {plan.when ? <span className={styles['planWhen']}> · {plan.when}</span> : null}
          </p>
        ) : null}

        {/* Рейка действий: то же, что свайп, — для desktop, клавиатуры и спокойного темпа */}
        <div className={styles['rail']}>
          <button
            type="button"
            className={cx(styles['action'], styles['pass'])}
            onClick={() => flyOut(-1)}
            disabled={busy}
            tabIndex={interactive ? 0 : -1}
            aria-label={`Пропустить ${card.display_name ?? ''}`}
          >
            <Icon name="close" size={20} />
            Мимо
          </button>

          <button
            type="button"
            className={cx(styles['action'], styles['go'])}
            onClick={() => flyOut(1)}
            disabled={busy}
            tabIndex={interactive ? 0 : -1}
            aria-label={`Го: ${card.display_name ?? ''}`}
          >
            {busy ? <Spinner size={18} /> : <Icon name="bolt" size={20} />}
            Го
          </button>
        </div>
      </div>

      {/* Вердикт жеста: слово выезжает из-за края, за ним тянутся линии скорости */}
      <span className={cx(styles['verdict'], styles['verdictGo'])} aria-hidden>
        <span className={styles['verdictLines']} />
        Го
      </span>
      <span className={cx(styles['verdict'], styles['verdictPass'])} aria-hidden>
        <span className={styles['verdictLines']} />
        Мимо
      </span>
    </article>
  )
}

/* ===================================================================== */
/* Строка плана: что делаем и когда оба свободны                          */
/* ===================================================================== */

/** Общее идёт первым: сначала совпавшие форматы, потом остальные; время — только общее. */
function planLine(card: UserCard, viewer: ViewerContext | undefined): { what: string; when: string } | null {
  const formats = card.friends?.formats ?? []
  if (!formats.length && !(card.friends?.rhythm ?? []).length) return null

  const rhythm = card.friends?.rhythm ?? []
  const sharedRhythm = rhythm.filter((slot) => viewer?.rhythm.includes(slot))
  const when = (sharedRhythm.length ? sharedRhythm : rhythm)
    .slice(0, 2)
    .map((s) => RHYTHM_LABEL[s])
    .join(', ')

  // Строка одна: со временем помещается один формат, без него — два. Остальное — в профиле.
  const common = formats.filter((f) => viewer?.formats.includes(f))
  const rest = formats.filter((f) => !viewer?.formats.includes(f))
  const what = [...common, ...rest].slice(0, when ? 1 : 2).join(' · ')

  return { what: what || '—', when: when.toLowerCase() }
}

/* ===================================================================== */
/* Скелетон загрузки                                                      */
/* ===================================================================== */

export function CandidateCardSkeleton({ className }: { className?: string }): React.ReactElement {
  return (
    <div className={cx(styles['skeletonCard'], className)} aria-hidden>
      <div className={styles['skeletonFrame']} />
      <div className={styles['skeletonBody']}>
        <div className={styles['skeletonLine']} style={{ width: '52%', height: 22 }} />
        <div className={styles['skeletonLine']} style={{ width: '82%' }} />
        <div className={styles['skeletonLine']} style={{ width: '64%' }} />
      </div>
    </div>
  )
}
